import {
  Activity,
  BarChart3,
  Bot,
  Brain,
  CalendarClock,
  CreditCard,
  FileText,
  Gauge,
  HeartPulse,
  History,
  ListChecks,
  MessageSquare,
  Plug,
  Radar,
  RefreshCcw,
  ScrollText,
  Settings,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  UserCog,
  Users
} from 'lucide-react'
import { ADMIN_CAPABILITIES } from './capabilities'

export const ADMIN_NAVIGATION_GROUPS = Object.freeze([
  {
    id: 'overview',
    label: 'Overview',
    items: [
      {
        id: 'overview-home',
        label: 'Home',
        path: '/admin',
        icon: Gauge,
        capability: ADMIN_CAPABILITIES.CONSOLE_READ,
        page: 'home',
        description: 'Platform operations at a glance',
        keywords: ['overview', 'attention', 'health', 'home']
      },
      {
        id: 'overview-analytics',
        label: 'Analytics',
        path: '/admin/analytics',
        icon: BarChart3,
        capability: ADMIN_CAPABILITIES.ANALYTICS_READ,
        page: 'analytics',
        description: 'Product intelligence and historical reliability',
        keywords: [
          'analytics',
          'events',
          'trace',
          'request',
          'dependencies',
          'authentication',
          'health'
        ]
      },
      {
        id: 'overview-legacy-analytics',
        label: 'Legacy Analytics',
        path: '/admin/analytics/legacy',
        icon: History,
        capability:
          ADMIN_CAPABILITIES.ANALYTICS_READ,
        page: 'analytics-legacy',
        badge: 'Deprecated',
        deprecated: true,
        description:
          'Legacy charts and sanitized request inspection',
        keywords: [
          'legacy',
          'deprecated',
          'charts',
          'pie',
          'payload',
          'request',
          'response'
        ]
      }
    ]
  },
  {
    id: 'customers',
    label: 'Customers',
    items: [
      {
        id: 'customers-users',
        label: 'Users',
        path: '/admin/users',
        icon: Users,
        capability: ADMIN_CAPABILITIES.USERS_READ,
        page: 'users',
        description: 'Approve and manage platform accounts',
        keywords: ['accounts', 'approval', 'customers']
      },
      {
        id: 'customers-subscriptions',
        label: 'Subscriptions',
        path: '/admin/subscriptions',
        icon: CreditCard,
        capability: ADMIN_CAPABILITIES.USERS_READ,
        page: 'subscriptions',
        description: 'Review plan and subscription state',
        keywords: ['plans', 'billing', 'tiers']
      }
    ]
  },
  {
    id: 'ai',
    label: 'AI',
    items: [
      {
        id: 'ai-signals',
        label: 'Market Monitor',
        path: '/admin/ai/market-monitor',
        icon: Sparkles,
        capability: ADMIN_CAPABILITIES.AI_READ,
        page: 'ai-signals',
        description: 'Manage multi-asset research and its runtime',
        keywords: [
          'commodities',
          'equities',
          'indices',
          'research',
          'scheduler',
          'cache'
        ]
      },
      {
        id: 'ai-agents',
        label: 'Agent Operations',
        path: '/admin/ai/agents',
        icon: Bot,
        capability: ADMIN_CAPABILITIES.AI_MANAGE,
        page: 'ai-agents',
        description: 'Inspect agent runtime and evaluated outcomes',
        keywords: ['langgraph', 'agents', 'accuracy', 'performance']
      },
      {
        id: 'ai-jobs',
        label: 'Jobs',
        path: '/admin/ai/jobs',
        icon: ListChecks,
        capability: ADMIN_CAPABILITIES.AI_MANAGE,
        page: 'ai-jobs',
        description: 'Inspect asynchronous AI workloads',
        keywords: ['workers', 'queue', 'generation']
      },
      {
        id: 'ai-usage',
        label: 'Usage And Cost',
        path: '/admin/ai/usage',
        icon: Activity,
        capability: ADMIN_CAPABILITIES.ANALYTICS_READ,
        page: 'ai-usage',
        description: 'Review AI usage and credit consumption',
        keywords: ['credits', 'tokens', 'cost']
      },
      {
        id: 'ai-providers',
        label: 'Provider Settings',
        path: '/admin/ai/providers',
        icon: Brain,
        capability: ADMIN_CAPABILITIES.AI_SETTINGS_MANAGE,
        page: 'ai-providers',
        description: 'Configure LLM providers by plan',
        keywords: ['models', 'openai', 'llm', 'configuration']
      }
    ]
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      {
        id: 'operations-control',
        label: 'Control Center',
        path: '/admin/operations',
        icon: Shield,
        capability: ADMIN_CAPABILITIES.OPERATIONS_READ,
        page: 'operations-control',
        description: 'Trading safety and reconciliation controls',
        keywords: ['kill switch', 'reconciliation', 'runtime']
      },
      {
        id: 'operations-jobs',
        label: 'Jobs And Queues',
        path: '/admin/operations/jobs',
        icon: ListChecks,
        capability: ADMIN_CAPABILITIES.OPERATIONS_READ,
        page: 'operations-jobs',
        description: 'Inspect outbox domains and queue health',
        keywords: ['outbox', 'workers', 'backlog', 'failed']
      },
      {
        id: 'operations-integrations',
        label: 'Integrations',
        path: '/admin/operations/integrations',
        icon: SlidersHorizontal,
        capability: ADMIN_CAPABILITIES.OPERATIONS_READ,
        page: 'operations-integrations',
        description: 'Monitor broker, market-data, and AI search services',
        keywords: [
          'angel one',
          'broker',
          'rates',
          'market',
          'searxng',
          'search'
        ]
      },
      {
        id: 'operations-rollover',
        label: 'Contract Rollover',
        path: '/admin/operations/contract-rollover',
        icon: RefreshCcw,
        capability: ADMIN_CAPABILITIES.OPERATIONS_READ,
        page: 'operations-rollover',
        description: 'Review and execute expiry transitions',
        keywords: ['contracts', 'expiry', 'positions', 'preview']
      },
      {
        id: 'operations-mcp',
        label: 'MCP Runtime',
        path: '/admin/operations/mcp',
        icon: Plug,
        capability: ADMIN_CAPABILITIES.OPERATIONS_READ,
        page: 'operations-mcp',
        description: 'Monitor MCP servers and tool execution',
        keywords: ['servers', 'tools', 'latency', 'failures']
      },
      {
        id: 'operations-observability',
        label: 'Observability',
        path: '/admin/operations/observability',
        icon: Radar,
        capability: ADMIN_CAPABILITIES.OPERATIONS_READ,
        page: 'operations-observability',
        description: 'Telemetry pipelines, alerts, coverage, and tools',
        keywords: [
          'telemetry',
          'prometheus',
          'grafana',
          'kibana',
          'jaeger',
          'alertmanager',
          'pipelines'
        ]
      },
      {
        id: 'operations-health',
        label: 'System Health',
        path: '/admin/operations/health',
        icon: HeartPulse,
        capability: ADMIN_CAPABILITIES.OPERATIONS_READ,
        page: 'operations-health',
        description: 'Review service health and runtime metrics',
        keywords: ['status', 'uptime', 'latency', 'incidents']
      }
    ]
  },
  {
    id: 'access-audit',
    label: 'Access And Audit',
    items: [
      {
        id: 'access-administrators',
        label: 'Administrators',
        path: '/admin/access',
        icon: UserCog,
        capability: ADMIN_CAPABILITIES.ACCESS_READ,
        page: 'access',
        description: 'Administrator accounts and role assignment',
        keywords: ['roles', 'permissions', 'mfa']
      },
      {
        id: 'access-audit',
        label: 'Audit Log',
        path: '/admin/audit',
        icon: ScrollText,
        capability: ADMIN_CAPABILITIES.AUDIT_READ,
        page: 'audit',
        description: 'Review privileged platform activity',
        keywords: ['history', 'actions', 'actors', 'changes']
      },
      {
        id: 'access-security',
        label: 'Security Events',
        path: '/admin/security',
        icon: ShieldCheck,
        capability: ADMIN_CAPABILITIES.SECURITY_READ,
        page: 'security',
        description: 'Review authentication and authorization events',
        keywords: ['denied', 'login', 'elevation', 'authentication']
      }
    ]
  },
  {
    id: 'content',
    label: 'Content',
    items: [
      {
        id: 'content-posts',
        label: 'Posts',
        path: '/admin/content/posts',
        icon: FileText,
        capability: ADMIN_CAPABILITIES.CONTENT_MANAGE,
        page: 'content-posts',
        description: 'Publish and manage blog posts',
        keywords: ['blog', 'articles', 'publishing']
      },
      {
        id: 'content-comments',
        label: 'Comments',
        path: '/admin/content/comments',
        icon: MessageSquare,
        capability: ADMIN_CAPABILITIES.CONTENT_MANAGE,
        page: 'content-comments',
        description: 'Moderate blog comments',
        keywords: ['blog', 'moderation', 'replies']
      },
      {
        id: 'content-ai-queue',
        label: 'AI Queue',
        path: '/admin/content/ai-queue',
        icon: Sparkles,
        capability: ADMIN_CAPABILITIES.CONTENT_MANAGE,
        page: 'content-ai-queue',
        description: 'Review queued AI content work',
        keywords: ['blog', 'generation', 'queue']
      },
      {
        id: 'content-schedules',
        label: 'Schedules',
        path: '/admin/content/schedules',
        icon: CalendarClock,
        capability: ADMIN_CAPABILITIES.CONTENT_MANAGE,
        page: 'content-schedules',
        description: 'Manage publishing schedules',
        keywords: ['blog', 'calendar', 'automation']
      },
      {
        id: 'content-analytics',
        label: 'Analytics',
        path: '/admin/content/analytics',
        icon: BarChart3,
        capability: ADMIN_CAPABILITIES.ANALYTICS_READ,
        page: 'content-analytics',
        description: 'Review content performance',
        keywords: ['blog', 'traffic', 'engagement']
      },
      {
        id: 'content-settings',
        label: 'Settings',
        path: '/admin/content/settings',
        icon: Settings,
        capability: ADMIN_CAPABILITIES.CONTENT_MANAGE,
        page: 'content-settings',
        description: 'Configure content publishing',
        keywords: ['blog', 'configuration']
      }
    ]
  }
])

export const ADMIN_NAVIGATION_ITEMS = Object.freeze(
  ADMIN_NAVIGATION_GROUPS.flatMap(group => group.items)
)

export function getAdminNavigation(capabilities = []) {
  const allowed = new Set(capabilities)

  return ADMIN_NAVIGATION_GROUPS.map(group => ({
    ...group,
    items: group.items.filter(item => allowed.has(item.capability))
  })).filter(group => group.items.length > 0)
}

export function getAdminNavigationItem(pathname) {
  return ADMIN_NAVIGATION_ITEMS.find(item => item.path === pathname) || null
}
